import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Loader2, Bot, MessageSquare, CheckCircle2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";

export default function Conversations() {
  const { sessionId } = useParams();
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null);
  const [activeMsgs, setActiveMsgs] = useState([]);

  useEffect(() => {
    api.get("/conversations").then(r => setItems(r.data)).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!sessionId) { setActive(null); setActiveMsgs([]); return; }
    api.get(`/conversations/${sessionId}`).then(r => {
      setActive(r.data.session); setActiveMsgs(r.data.messages);
    });
  }, [sessionId]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-[360px_1fr] h-[calc(100vh-4rem)]" data-testid="conversations-page">
      <aside className="border-r border-white/10 flex flex-col min-h-0">
        <div className="px-5 h-14 flex items-center justify-between border-b border-white/10">
          <div className="font-display text-lg tracking-tight">Conversations</div>
          <span className="text-xs text-zinc-500">{items.length}</span>
        </div>
        <div className="flex-1 overflow-y-auto">
          {loading ? <div className="p-6 text-zinc-500"><Loader2 className="w-4 h-4 animate-spin"/></div>
            : items.length === 0 ? <div className="p-8 text-center text-sm text-zinc-500">No conversations yet. Share your agent link to get started.</div>
            : items.map(c => {
              const captured = c.captured || {};
              const name = captured.name || "Anonymous";
              return (
                <button key={c.id} onClick={() => nav(`/app/conversations/${c.id}`)}
                  className={`w-full text-left px-5 py-3 border-b border-white/5 hover:bg-white/5 transition ${active?.id === c.id ? "bg-white/5" : ""}`}
                  data-testid={`conv-${c.id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-full bg-gradient-to-br from-emerald-500/30 to-emerald-500/5 border border-emerald-500/20 grid place-items-center font-display text-sm shrink-0">
                      {name[0].toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center justify-between">
                        <div className="text-sm font-medium truncate">{name}</div>
                        {c.complete && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0 ml-2"/>}
                      </div>
                      <div className="text-xs text-zinc-500 truncate mt-0.5">{c.last_message?.text || "—"}</div>
                      <div className="text-[10px] text-zinc-600 mt-0.5">{new Date(c.last_activity).toLocaleString()}</div>
                    </div>
                  </div>
                </button>
              );
            })}
        </div>
      </aside>

      <section className="flex flex-col min-h-0">
        {!active ? (
          <div className="flex-1 grid place-items-center text-center p-10">
            <div>
              <MessageSquare className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
              <div className="font-display text-lg">Pick a conversation</div>
              <div className="text-zinc-500 text-sm">View transcripts of every chat your AI agent has handled.</div>
            </div>
          </div>
        ) : (
          <>
            <div className="border-b border-white/10 p-5">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs tracking-[0.2em] uppercase text-zinc-500">Captured info</div>
                  <div className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                    {Object.keys(active.captured || {}).length === 0 && <div className="text-zinc-500 text-xs">Nothing captured yet</div>}
                    {Object.entries(active.captured || {}).map(([k, v]) => (
                      <div key={k}><span className="text-zinc-500 text-xs">{k}:</span> <span className="text-white">{String(v)}</span></div>
                    ))}
                  </div>
                </div>
                {active.complete && <Badge className="bg-emerald-500/15 text-emerald-300 border-emerald-500/30">Complete</Badge>}
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-6 space-y-3">
              {activeMsgs.map(m => (
                <div key={m.id} className={`flex ${m.role === "customer" ? "justify-end" : "justify-start"}`}>
                  <div className={`max-w-[75%] rounded-2xl px-4 py-2.5 border text-sm ${m.role === "customer" ? "bg-emerald-500/15 border-emerald-500/30" : "bg-zinc-900 border-white/10"}`}>
                    <div>{m.text}</div>
                    {m.role === "ai" && <div className="mt-1 text-[10px] text-emerald-300/80 flex items-center gap-1"><Bot className="w-3 h-3"/> AI</div>}
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
